import React from 'react';
import { ArrowRight, TrendingUp, TrendingDown } from 'lucide-react';

/**
 * WhyPanel — shared synthesized "why your X is where it is" panel.
 *
 * Used by Recovery, Movement, Mental and Nutrition pillar pages. Single
 * layout: ONE headline + ONE action + ranked driver chips with color-coded
 * ±impact.
 *
 * Props:
 *   - insight: {headline, action, drivers: [{name, impact, detail}]} | null
 *   - icon:    lucide-react icon component
 *   - color:   accent color (e.g. '#22d3ee' for recovery, '#CCFF00' for movement)
 *   - title:   header label e.g. "WHY YOUR RECOVERY IS WHERE IT IS"
 *   - score:   pillar score for the header tail ("WHY YOUR RECOVERY IS 65")
 *   - testIdPrefix:  used to namespace all data-testids
 *   - onActionClick: optional handler for the action CTA
 *   - emptyMessage:  fallback when insight is null
 */

const DriverChip = ({ driver, testId }) => {
  const positive = driver.impact >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const color = positive ? '#a3e635' : '#fb923c';
  return (
    <div
      className="rounded-sm border bg-zinc-900/40 p-3 flex items-start gap-2.5"
      style={{ borderColor: `${color}33` }}
      data-testid={testId}
    >
      <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-xs font-secondary font-bold text-white">{driver.name}</p>
          <p className="text-[10px] font-secondary font-bold tabular-nums" style={{ color }}>
            {positive ? '+' : ''}{driver.impact}
          </p>
        </div>
        <p className="text-[11px] text-zinc-400 leading-snug mt-1">{driver.detail}</p>
      </div>
    </div>
  );
};

const WhyPanel = ({
  insight, icon: Icon, color, title, score, testIdPrefix,
  childTestIdPrefix,
  onActionClick, actionLabel, emptyMessage,
}) => {
  const cp = childTestIdPrefix || testIdPrefix;
  if (!insight) {
    return (
      <div className="hpme-card p-5" data-testid={`${testIdPrefix}-empty`}>
        <div className="hpme-card-corner" />
        <div className="flex items-center gap-2 mb-2">
          {Icon && <Icon className="w-4 h-4" style={{ color }} />}
          <span className="text-[10px] uppercase tracking-[0.2em] font-secondary font-bold" style={{ color }}>{title}</span>
        </div>
        <p className="text-xs text-zinc-400 leading-relaxed">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="hpme-card p-5" data-testid={testIdPrefix}>
      <div className="hpme-card-corner" />
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4" style={{ color }} />}
          <span className="text-[10px] uppercase tracking-[0.2em] font-secondary font-bold" style={{ color }}>
            {title} {score != null ? score : ''}
          </span>
        </div>
      </div>

      <p className="font-secondary font-bold text-lg text-white leading-snug mb-2" data-testid={`${cp}-headline`}>{insight.headline}</p>
      <p className="text-xs text-zinc-300 leading-relaxed mb-4 border-l-2 pl-3" style={{ borderColor: color }} data-testid={`${cp}-action`}>{insight.action}</p>

      {insight.drivers && insight.drivers.length > 0 && (
        <div className="space-y-2" data-testid={`${cp}-drivers`}>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-secondary mb-1">DRIVERS · WORST → BEST</p>
          {insight.drivers.map((d, i) => (
            <DriverChip key={i} driver={d} testId={`${cp}-driver-${i}`} />
          ))}
        </div>
      )}

      {onActionClick && (
        <button
          type="button"
          onClick={onActionClick}
          className="mt-4 inline-flex items-center justify-center gap-1.5 px-4 py-2 border text-xs font-secondary font-bold uppercase tracking-wider rounded-sm transition-colors"
          style={{ color, borderColor: `${color}66`, backgroundColor: `${color}1A` }}
          data-testid={`${cp}-action-btn`}
        >
          {actionLabel || 'Talk to Nikki about this'} <ArrowRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

export default WhyPanel;
